import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  BookOpen,
  Plus,
  Heart,
  MessageCircle,
  Edit,
  Trash2,
  Eye,
  Calendar,
  Sparkles,
  TrendingUp,
  Loader2,
  PenTool,
  Image as ImageIcon,
  ShoppingBag,
  Upload,
  CheckCircle,
  XCircle,
  Clock,
  IndianRupee,
  Package,
  Coffee,
  Shirt,
  Frame,
  Mouse,
  ChevronLeft,
  ChevronRight,
  Info,
  Calculator,
  Globe,
  Lock
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

// --- Merch Product Types Config ---
const PRODUCT_TYPES = [
  {
    id: 'mugs', label: 'Mug', Icon: Coffee,
    templateWidth: 400, templateHeight: 300,
    artArea: { x: 80, y: 40, w: 240, h: 200 },
    color: 'from-amber-500 to-orange-500'
  },
  {
    id: 'tshirts', label: 'T-Shirt', Icon: Shirt,
    templateWidth: 350, templateHeight: 420,
    artArea: { x: 90, y: 80, w: 170, h: 200 },
    color: 'from-blue-500 to-indigo-500'
  },
  {
    id: 'posters', label: 'Poster', Icon: Frame,
    templateWidth: 300, templateHeight: 420,
    artArea: { x: 20, y: 20, w: 260, h: 380 },
    color: 'from-purple-500 to-pink-500'
  },
  {
    id: 'mousepads', label: 'Mousepad', Icon: Mouse,
    templateWidth: 400, templateHeight: 300,
    artArea: { x: 30, y: 30, w: 340, h: 240 },
    color: 'from-emerald-500 to-teal-500'
  },
];

// --- Canvas Mockup Component (with Printful Support) ---
const MockupPreview = ({ productType, artImage, mockupData, mockupLoading }) => {
  const canvasRef = useRef(null);
  const [activeView, setActiveView] = useState(0);
  const config = PRODUCT_TYPES.find(p => p.id === productType);

  // Build all view URLs from mockup data
  const allViews = React.useMemo(() => {
    if (!mockupData || mockupData.length === 0) return [];
    const views = [];
    for (const m of mockupData) {
      if (m.mockup_url) {
        views.push({ url: m.mockup_url, title: 'Main View' });
      }
      if (m.extra && Array.isArray(m.extra)) {
        for (const e of m.extra) {
          if (e.url) views.push({ url: e.url, title: e.title || 'Extra View' });
        }
      }
    }
    return views;
  }, [mockupData]);

  const showPrintful = allViews.length > 0 && !mockupLoading;

  // Reset active view when views change
  useEffect(() => {
    setActiveView(0);
  }, [allViews.length]);

  // Canvas-based fallback rendering — always call this hook (Rules of Hooks)
  useEffect(() => {
    if (showPrintful) return; // Skip canvas drawing when showing Printful image
    if (!canvasRef.current || !config) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    canvas.width = config.templateWidth;
    canvas.height = config.templateHeight;

    // Clear
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw product template background
    ctx.fillStyle = '#f3f4f6';
    ctx.strokeStyle = '#d1d5db';
    ctx.lineWidth = 2;

    if (productType === 'mugs') {
      ctx.save();
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.roundRect(60, 30, 220, 230, 12);
      ctx.fill();
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(285, 140, 40, -Math.PI / 2, Math.PI / 2);
      ctx.lineWidth = 8;
      ctx.strokeStyle = '#d1d5db';
      ctx.stroke();
      ctx.restore();
    } else if (productType === 'tshirts') {
      ctx.save();
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.moveTo(100, 20);
      ctx.lineTo(30, 80);
      ctx.lineTo(30, 140);
      ctx.lineTo(70, 130);
      ctx.lineTo(70, 400);
      ctx.lineTo(280, 400);
      ctx.lineTo(280, 130);
      ctx.lineTo(320, 140);
      ctx.lineTo(320, 80);
      ctx.lineTo(250, 20);
      ctx.quadraticCurveTo(175, 60, 100, 20);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      ctx.restore();
    } else if (productType === 'posters') {
      ctx.save();
      ctx.fillStyle = '#ffffff';
      ctx.shadowColor = 'rgba(0,0,0,0.15)';
      ctx.shadowBlur = 15;
      ctx.fillRect(10, 10, 280, 400);
      ctx.restore();
      ctx.strokeRect(10, 10, 280, 400);
    } else if (productType === 'mousepads') {
      ctx.save();
      ctx.fillStyle = '#333';
      ctx.beginPath();
      ctx.roundRect(15, 15, 370, 270, 16);
      ctx.fill();
      ctx.fillStyle = '#555';
      ctx.beginPath();
      ctx.roundRect(20, 20, 360, 260, 12);
      ctx.fill();
      ctx.restore();
    }

    // Draw art image overlay
    if (artImage) {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const { x, y, w, h } = config.artArea;
        const scale = Math.min(w / img.width, h / img.height);
        const drawW = img.width * scale;
        const drawH = img.height * scale;
        const drawX = x + (w - drawW) / 2;
        const drawY = y + (h - drawH) / 2;
        ctx.save();
        if (productType === 'mugs' || productType === 'mousepads') {
          ctx.globalAlpha = 0.9;
        }
        ctx.drawImage(img, drawX, drawY, drawW, drawH);
        ctx.restore();
      };
      img.src = artImage;
    } else {
      const { x, y, w, h } = config.artArea;
      ctx.save();
      ctx.fillStyle = 'rgba(139, 92, 246, 0.1)';
      ctx.strokeStyle = 'rgba(139, 92, 246, 0.3)';
      ctx.setLineDash([6, 4]);
      ctx.fillRect(x, y, w, h);
      ctx.strokeRect(x, y, w, h);
      ctx.setLineDash([]);
      ctx.fillStyle = 'rgba(139, 92, 246, 0.5)';
      ctx.font = '14px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('Your Art Here', x + w / 2, y + h / 2);
      ctx.restore();
    }
  }, [productType, artImage, config, showPrintful]);

  if (!config) return null;

  // Printful photorealistic mockup mode — multi-angle gallery
  if (showPrintful) {
    const currentView = allViews[activeView] || allViews[0];
    const canGoLeft = activeView > 0;
    const canGoRight = activeView < allViews.length - 1;

    return (
      <div className="relative bg-gray-800/50 rounded-2xl p-4 flex flex-col items-center">
        {/* Main mockup image */}
        <div className="relative w-full flex items-center justify-center" style={{ minHeight: 280 }}>
          {/* Left arrow */}
          {allViews.length > 1 && (
            <button
              onClick={() => canGoLeft && setActiveView(activeView - 1)}
              className={`absolute left-1 z-10 p-1.5 rounded-full transition-all ${canGoLeft
                ? 'bg-gray-700/80 hover:bg-purple-600/80 text-white cursor-pointer'
                : 'bg-gray-800/40 text-gray-600 cursor-default'
                }`}
              disabled={!canGoLeft}
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
          )}

          <img
            src={currentView?.url}
            alt={`${config?.label || 'Product'} - ${currentView?.title || 'Mockup'}`}
            className="rounded-xl max-w-full object-contain"
            style={{ maxHeight: 300, width: 'auto' }}
          />

          {/* Right arrow */}
          {allViews.length > 1 && (
            <button
              onClick={() => canGoRight && setActiveView(activeView + 1)}
              className={`absolute right-1 z-10 p-1.5 rounded-full transition-all ${canGoRight
                ? 'bg-gray-700/80 hover:bg-purple-600/80 text-white cursor-pointer'
                : 'bg-gray-800/40 text-gray-600 cursor-default'
                }`}
              disabled={!canGoRight}
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Thumbnail strip */}
        {allViews.length > 1 && (
          <div className="flex gap-2 mt-3 overflow-x-auto pb-1">
            {allViews.map((view, idx) => (
              <button
                key={idx}
                onClick={() => setActiveView(idx)}
                className={`relative flex-shrink-0 rounded-lg overflow-hidden border-2 transition-all ${idx === activeView
                  ? 'border-purple-500 ring-1 ring-purple-400/50'
                  : 'border-gray-700 hover:border-gray-500 opacity-60 hover:opacity-100'
                  }`}
                style={{ width: 56, height: 56 }}
              >
                <img
                  src={view.url}
                  alt={view.title}
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>
        )}

        {/* Labels */}
        <div className="flex items-center justify-between w-full mt-2">
          <Badge className="bg-green-500/20 text-green-300 border border-green-500/30 text-xs px-2 py-0.5">
            ✨ Printful Preview
          </Badge>
          <span className="text-xs text-gray-400">
            {currentView?.title}{allViews.length > 1 ? ` (${activeView + 1}/${allViews.length})` : ''}
          </span>
        </div>
      </div>
    );
  }

  // Canvas fallback mode
  return (
    <div className="relative bg-gray-800/50 rounded-2xl p-6 flex items-center justify-center">
      <canvas
        ref={canvasRef}
        className="rounded-xl max-w-full"
        style={{ maxHeight: 350, width: 'auto' }}
      />
      {mockupLoading && (
        <div className="absolute inset-0 bg-gray-900/60 rounded-2xl flex flex-col items-center justify-center backdrop-blur-sm">
          <Loader2 className="w-8 h-8 text-purple-400 animate-spin mb-3" />
          <span className="text-purple-300 text-sm font-medium">Generating Printful mockup...</span>
          <span className="text-gray-500 text-xs mt-1">This takes ~10-15 seconds</span>
        </div>
      )}
      <div className="absolute bottom-3 right-3 text-xs text-gray-500 bg-gray-900/80 px-2 py-1 rounded">
        {mockupLoading ? 'Generating...' : 'Preview'}
      </div>
    </div>
  );
};

// --- Merch Tab Component ---
const MerchTab = ({ token, API, user }) => {
  const [selectedType, setSelectedType] = useState('mugs');
  const [primaryArt, setPrimaryArt] = useState(null);         // Default art for all variants
  const [variantArt, setVariantArt] = useState({});            // { "11oz": url, "15oz": url }
  const [uploading, setUploading] = useState(false);
  const [variantUploading, setVariantUploading] = useState({}); // { "11oz": true }
  const [productName, setProductName] = useState('');
  const [productDesc, setProductDesc] = useState('');
  const [creatorMarkup, setCreatorMarkup] = useState(100);
  const [submitting, setSubmitting] = useState(false);
  const [myProducts, setMyProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [basePrices, setBasePrices] = useState({});
  const [printfulMockupData, setPrintfulMockupData] = useState(null);
  const [mockupLoading, setMockupLoading] = useState(false);
  const [catalog, setCatalog] = useState(null);                // Dynamic Printful catalog
  const [availability, setAvailability] = useState({});        // Stock per variant
  const [activeVariant, setActiveVariant] = useState(null);    // Selected variant for preview
  const [showPerVariant, setShowPerVariant] = useState(false); // Toggle per-variant customization
  const [colorVariants, setColorVariants] = useState(null);    // All colors/sizes from Printful
  const [selectedColor, setSelectedColor] = useState('Black'); // Currently selected color
  const [colorSearchTerm, setColorSearchTerm] = useState('');  // Search filter for colors
  const [loadingColors, setLoadingColors] = useState(false);
  const fileInputRef = useRef(null);
  const variantFileRefs = useRef({});
  const mockupPollRef = useRef(null);

  const [costData, setCostData] = useState(null);
  const [costLoading, setCostLoading] = useState(false);

  const basePrice = basePrices[selectedType] || 450;
  const totalPrice = basePrice + creatorMarkup;

  // Get catalog data for selected product
  const catalogProduct = catalog?.[selectedType];

  // Use color-specific variants if available, otherwise fall back to catalog variants
  const colorData = colorVariants?.colors?.[selectedColor];
  const variants = colorData?.sizes || catalogProduct?.variants || {};
  const variantKeys = Object.keys(variants);

  // Fetch base prices, my submissions, and Printful catalog
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [pricesRes, productsRes, catalogRes] = await Promise.all([
          axios.get(`${API}/store/base-prices`),
          axios.get(`${API}/store/user-products/me`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${API}/printful/catalog`).catch(() => null),
        ]);
        setBasePrices(pricesRes.data.base_prices || {});
        setMyProducts(productsRes.data.products || []);
        if (catalogRes?.data?.catalog) {
          setCatalog(catalogRes.data.catalog);
        }
      } catch (e) {
        console.error('Merch data fetch error:', e);
      } finally {
        setLoadingProducts(false);
      }
    };
    fetchData();
  }, [API, token]);

  // Fetch availability and color variants when product type changes
  useEffect(() => {
    setPrintfulMockupData(null);
    setVariantArt({});
    setShowPerVariant(false);
    setActiveVariant(null);
    setColorVariants(null);
    setColorSearchTerm('');

    const fetchAvailability = async () => {
      try {
        const res = await axios.get(`${API}/printful/availability/${selectedType}`);
        setAvailability(res.data.availability || {});
      } catch (e) {
        console.error('Availability fetch error:', e);
        setAvailability({});
      }
    };
    fetchAvailability();

    // Fetch all color variants for this product
    const fetchColorVariants = async () => {
      setLoadingColors(true);
      try {
        const res = await axios.get(`${API}/printful/product-variants/${selectedType}`);
        const data = res.data.variants_by_color;
        setColorVariants(data);
        // Default to Black if available, otherwise first color
        if (data?.colors) {
          const colorNames = Object.keys(data.colors);
          setSelectedColor(colorNames.includes('Black') ? 'Black' : colorNames[0]);
        }
      } catch (e) {
        console.error('Color variants fetch error:', e);
        setColorVariants(null);
      } finally {
        setLoadingColors(false);
      }
    };
    fetchColorVariants();

    // Fetch cost breakdown for this product type
    const fetchCosts = async () => {
      setCostLoading(true);
      try {
        const res = await axios.get(`${API}/printful/product-costs/${selectedType}`);
        setCostData(res.data);
      } catch (e) {
        console.error('Cost data fetch error:', e);
        setCostData(null);
      } finally {
        setCostLoading(false);
      }
    };
    fetchCosts();

    // Re-generate mockup if art exists
    if (primaryArt) {
      generatePrintfulMockup(primaryArt, selectedType);
    }

    return () => {
      if (mockupPollRef.current) {
        clearInterval(mockupPollRef.current);
      }
    };
  }, [selectedType]);

  // Set default active variant when catalog loads or product changes
  useEffect(() => {
    if (catalogProduct && !activeVariant) {
      setActiveVariant(catalogProduct.default_variant || variantKeys[0]);
    }
  }, [catalogProduct, selectedType]);

  // Get art URL for a specific variant (per-variant override or primary fallback)
  const getArtForVariant = (variantKey) => {
    return variantArt[variantKey] || primaryArt;
  };

  // Handle primary art upload
  const handleImageUpload = async (file) => {
    if (!file || !file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be under 10MB');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/upload/image`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      const uploadedUrl = res.data.url || res.data.secure_url;
      setPrimaryArt(uploadedUrl);
      toast.success('Art uploaded!');
      generatePrintfulMockup(uploadedUrl, selectedType);
    } catch (e) {
      console.error('Upload error:', e);
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  // Handle per-variant art upload
  const handleVariantUpload = async (file, variantKey) => {
    if (!file || !file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be under 10MB');
      return;
    }

    setVariantUploading(prev => ({ ...prev, [variantKey]: true }));
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/upload/image`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      const uploadedUrl = res.data.url || res.data.secure_url;
      setVariantArt(prev => ({ ...prev, [variantKey]: uploadedUrl }));
      toast.success(`Art for ${variantKey} uploaded!`);

      // Re-generate mockup if this is the active variant
      if (variantKey === activeVariant) {
        generatePrintfulMockup(uploadedUrl, selectedType, variantKey);
      }
    } catch (e) {
      console.error('Variant upload error:', e);
      toast.error('Failed to upload image');
    } finally {
      setVariantUploading(prev => ({ ...prev, [variantKey]: false }));
    }
  };

  // Generate Printful mockup (async with polling)
  const generatePrintfulMockup = async (artUrl, productType, variantSize) => {
    if (!artUrl) return;
    setPrintfulMockupData(null);
    setMockupLoading(true);

    if (mockupPollRef.current) {
      clearInterval(mockupPollRef.current);
      mockupPollRef.current = null;
    }

    try {
      const res = await axios.post(`${API}/printful/generate-mockup-async`, {
        art_url: artUrl,
        product_type: productType,
        variant_size: variantSize || undefined,
      });

      if (res.data.status === 'completed') {
        if (res.data.mockups?.length > 0) {
          setPrintfulMockupData(res.data.mockups);
          toast.success('Printful mockup ready!');
        }
        setMockupLoading(false);
        return;
      }

      const taskKey = res.data.task_key;
      const cacheKey = res.data.cache_key || '';

      if (!taskKey) {
        setMockupLoading(false);
        return;
      }

      let pollCount = 0;
      mockupPollRef.current = setInterval(async () => {
        pollCount++;
        if (pollCount > 25) {
          clearInterval(mockupPollRef.current);
          mockupPollRef.current = null;
          setMockupLoading(false);
          toast.error('Mockup generation timed out');
          return;
        }

        try {
          const pollRes = await axios.get(
            `${API}/printful/mockup-status/${taskKey}`,
            { params: { cache_key: cacheKey } }
          );

          if (pollRes.data.status === 'completed') {
            clearInterval(mockupPollRef.current);
            mockupPollRef.current = null;
            if (pollRes.data.mockups?.length > 0) {
              setPrintfulMockupData(pollRes.data.mockups);
              toast.success('Printful mockup ready!');
            }
            setMockupLoading(false);
          } else if (pollRes.data.status === 'failed') {
            clearInterval(mockupPollRef.current);
            mockupPollRef.current = null;
            setMockupLoading(false);
            console.error('Printful mockup failed:', pollRes.data.error);
          }
        } catch (pollErr) {
          console.error('Mockup poll error:', pollErr);
        }
      }, 3000);
    } catch (e) {
      console.error('Printful mockup error:', e);
      setMockupLoading(false);
    }
  };

  // When active variant changes, regenerate mockup with variant-specific art
  useEffect(() => {
    if (activeVariant && (primaryArt || variantArt[activeVariant])) {
      const art = getArtForVariant(activeVariant);
      if (art) generatePrintfulMockup(art, selectedType, activeVariant);
    }
  }, [activeVariant]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0];
    if (file) handleImageUpload(file);
  }, []);

  // Get stock badge for a variant
  const getStockBadge = (variantKey) => {
    const stock = availability[variantKey];
    // Also check color-specific variant data as fallback
    const colorStock = colorData?.sizes?.[variantKey];
    if (!stock && !colorStock) return { label: 'Checking...', color: 'text-gray-400 bg-gray-700/50', dot: 'bg-gray-400' };
    const inStock = stock?.in_stock ?? colorStock?.in_stock ?? true;
    if (inStock === false) return { label: 'Out of Stock', color: 'text-red-400 bg-red-500/20', dot: 'bg-red-400' };
    if (stock?.availability_status) {
      // availability_status can be an array of { region, status } or a string
      const statuses = Array.isArray(stock.availability_status)
        ? stock.availability_status.map(s => s.status || s)
        : [stock.availability_status];
      if (statuses.some(s => s === 'discontinued')) return { label: 'Discontinued', color: 'text-red-400 bg-red-500/20', dot: 'bg-red-400' };
      if (statuses.some(s => s === 'supplier_out_of_stock')) return { label: 'Low Stock', color: 'text-orange-400 bg-orange-500/20', dot: 'bg-orange-400' };
      if (statuses.every(s => s === 'stocked_on_demand')) return { label: 'On Demand', color: 'text-yellow-400 bg-yellow-500/20', dot: 'bg-yellow-400' };
    }
    return { label: 'In Stock', color: 'text-green-400 bg-green-500/20', dot: 'bg-green-400' };
  };

  const handleSubmit = async () => {
    if (!primaryArt && Object.keys(variantArt).length === 0) {
      toast.error('Please upload your art image');
      return;
    }
    if (!productName.trim()) { toast.error('Please enter a product name'); return; }

    setSubmitting(true);
    try {
      // Build per-variant image map
      const variantImages = {};
      for (const vk of variantKeys) {
        variantImages[vk] = getArtForVariant(vk) || primaryArt;
      }

      const res = await axios.post(`${API}/store/user-products`, {
        name: productName,
        description: productDesc,
        category: selectedType,
        product_type: selectedType,
        image_url: primaryArt,
        mockup_url: printfulMockupData?.[0]?.mockup_url || primaryArt,
        images: (() => {
          if (!printfulMockupData) return [];
          const urls = [];
          for (const m of printfulMockupData) {
            if (m.mockup_url) urls.push(m.mockup_url);
            if (m.extra) m.extra.forEach(e => { if (e.url) urls.push(e.url); });
          }
          return urls;
        })(),
        creator_markup: creatorMarkup,
        variant_images: variantImages,
        selected_color: selectedColor,
        variants: variantKeys.length > 1 ?
          [{ type: 'size', options: variantKeys }] : []
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.data.success) {
        toast.success('Product submitted for review!');
        setMyProducts(prev => [res.data.product, ...prev]);
        setPrimaryArt(null);
        setVariantArt({});
        setPrintfulMockupData(null);
        setProductName('');
        setProductDesc('');
        setCreatorMarkup(100);
        setShowPerVariant(false);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit product');
    } finally {
      setSubmitting(false);
    }
  };

  const statusConfig = {
    pending_review: { icon: Clock, label: 'Pending Review', color: 'text-yellow-400 bg-yellow-500/20 border-yellow-500/30' },
    approved: { icon: CheckCircle, label: 'Approved', color: 'text-green-400 bg-green-500/20 border-green-500/30' },
    rejected: { icon: XCircle, label: 'Rejected', color: 'text-red-400 bg-red-500/20 border-red-500/30' },
  };

  return (
    <div className="space-y-8">
      {/* Create Merch Section */}
      <div className="glass-card p-8 rounded-2xl neon-border">
        <h2 className="text-3xl font-bold text-white flex items-center mb-2">
          <ShoppingBag className="w-8 h-8 mr-3 text-amber-400" />
          Create Merchandise
        </h2>
        <p className="text-gray-400 mb-8">Upload your artwork and see it on real products! Set your price and submit for review.</p>

        {/* Step 1: Product Type + Printful Name */}
        <div className="mb-8">
          <h3 className="text-white font-semibold mb-3 flex items-center">
            <span className="w-7 h-7 bg-purple-600 rounded-full text-white text-sm flex items-center justify-center mr-2">1</span>
            Choose Product Type
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {PRODUCT_TYPES.map(pt => {
              const catInfo = catalog?.[pt.id];
              return (
                <button
                  key={pt.id}
                  onClick={() => setSelectedType(pt.id)}
                  className={`p-4 rounded-xl border-2 transition-all text-center ${selectedType === pt.id
                    ? 'border-purple-500 bg-purple-500/20 scale-[1.02]'
                    : 'border-gray-700 bg-gray-800/50 hover:border-gray-600'
                    }`}
                >
                  <pt.Icon className={`w-8 h-8 mx-auto mb-2 ${selectedType === pt.id ? 'text-purple-300' : 'text-gray-500'}`} />
                  <span className={`text-sm font-semibold ${selectedType === pt.id ? 'text-purple-300' : 'text-gray-400'}`}>
                    {catInfo?.name || pt.label}
                  </span>
                  <span className="block text-xs text-gray-500 mt-1">Base ₹{basePrices[pt.id] || '...'}</span>
                </button>
              );
            })}
          </div>

          {/* Printful product description */}
          {catalogProduct?.description && (
            <p className="text-gray-500 text-xs mt-3 italic leading-relaxed line-clamp-2">
              {catalogProduct.description.replace(/<[^>]+>/g, '').substring(0, 200)}
            </p>
          )}
        </div>

        {/* Step 1.5: Color Selector (for products with multiple colors) */}
        {colorVariants && Object.keys(colorVariants.colors || {}).length > 1 && (
          <div className="mb-8">
            <h3 className="text-white font-semibold mb-3 flex items-center">
              <span className="w-7 h-7 bg-purple-600 rounded-full text-white text-sm flex items-center justify-center mr-2">⬤</span>
              Choose Color
              <span className="ml-2 text-xs text-gray-500 font-normal">
                {Object.keys(colorVariants.colors).length} colors available
              </span>
            </h3>

            {/* Color search */}
            <div className="relative mb-3">
              <input
                type="text"
                placeholder="Search colors..."
                value={colorSearchTerm}
                onChange={(e) => setColorSearchTerm(e.target.value)}
                className="w-full bg-gray-800/60 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none"
              />
              {colorSearchTerm && (
                <button
                  onClick={() => setColorSearchTerm('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 text-xs"
                >
                  ✕
                </button>
              )}
            </div>

            {/* Color swatch grid */}
            {loadingColors ? (
              <div className="flex items-center gap-2 text-gray-400 text-sm py-4">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading colors...
              </div>
            ) : (
              <div className="max-h-48 overflow-y-auto pr-1 scrollbar-thin" style={{ scrollbarWidth: 'thin' }}>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(colorVariants.colors)
                    .filter(([name]) => name.toLowerCase().includes(colorSearchTerm.toLowerCase()))
                    .map(([colorName, colorInfo]) => (
                      <button
                        key={colorName}
                        onClick={() => {
                          setSelectedColor(colorName);
                          setActiveVariant(null);
                          setVariantArt({});
                        }}
                        title={colorName}
                        className={`group relative flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-medium transition-all ${selectedColor === colorName
                          ? 'border-purple-500 bg-purple-500/20 text-purple-300 ring-1 ring-purple-500/40'
                          : 'border-gray-700 bg-gray-800/50 text-gray-400 hover:border-gray-600 hover:text-gray-300'
                          }`}
                      >
                        <span
                          className="w-4 h-4 rounded-full border border-gray-600 flex-shrink-0"
                          style={{
                            backgroundColor: colorInfo.color_code || '#333',
                            ...(colorInfo.color_code2 ? {
                              background: `linear-gradient(135deg, ${colorInfo.color_code} 50%, ${colorInfo.color_code2} 50%)`
                            } : {})
                          }}
                        />
                        <span className="truncate max-w-[100px]">{colorName}</span>
                        <span className="text-[10px] text-gray-600 ml-0.5">
                          {Object.keys(colorInfo.sizes).length} sizes
                        </span>
                      </button>
                    ))
                  }
                </div>
              </div>
            )}

            {/* Selected color info */}
            {selectedColor && colorData && (
              <div className="mt-3 flex items-center gap-3 p-2 rounded-lg bg-gray-800/40 border border-gray-700">
                <span
                  className="w-8 h-8 rounded-lg border border-gray-600"
                  style={{ backgroundColor: colorData.color_code || '#333' }}
                />
                <div>
                  <p className="text-white text-sm font-medium">{selectedColor}</p>
                  <p className="text-gray-500 text-xs">Sizes: {Object.keys(colorData.sizes).join(', ')}</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 2: Upload Art + Per-Variant customization + Preview */}
        <div className="mb-8">
          <h3 className="text-white font-semibold mb-3 flex items-center">
            <span className="w-7 h-7 bg-purple-600 rounded-full text-white text-sm flex items-center justify-center mr-2">2</span>
            Upload Your Art & Preview
          </h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left: Upload Zone + Variant Cards */}
            <div className="space-y-4">
              {/* Primary Art Upload */}
              <div
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-gray-600 rounded-2xl p-6 flex flex-col items-center justify-center cursor-pointer hover:border-purple-500/50 transition-all min-h-[180px] bg-gray-800/30"
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleImageUpload(e.target.files[0])}
                />
                {uploading ? (
                  <Loader2 className="w-10 h-10 text-purple-400 animate-spin" />
                ) : primaryArt ? (
                  <div className="text-center">
                    <img src={primaryArt} alt="Primary art" className="w-32 h-32 object-contain rounded-lg mb-2 mx-auto" />
                    <p className="text-green-400 text-sm font-medium"><CheckCircle className="w-4 h-4 inline mr-1" /> Primary Art Uploaded</p>
                    <p className="text-gray-500 text-xs mt-1">Used for all variants by default • Click to change</p>
                  </div>
                ) : (
                  <>
                    <Upload className="w-10 h-10 text-gray-500 mb-2" />
                    <p className="text-gray-400 font-medium text-sm">Drag & drop your artwork</p>
                    <p className="text-gray-500 text-xs mt-1">PNG, JPG, max 10MB • Applied to all variants</p>
                  </>
                )}
              </div>

              {/* Per-Variant Customization Toggle */}
              {primaryArt && variantKeys.length > 1 && (
                <button
                  onClick={() => setShowPerVariant(!showPerVariant)}
                  className="w-full text-left text-sm text-purple-400 hover:text-purple-300 px-3 py-2 rounded-lg bg-purple-500/10 border border-purple-500/20 transition-all flex items-center justify-between"
                >
                  <span>{showPerVariant ? '▾' : '▸'} Customize art per variant ({variantKeys.length} variants)</span>
                  {Object.keys(variantArt).length > 0 && (
                    <Badge className="bg-purple-500/30 text-purple-300 text-xs">{Object.keys(variantArt).length} custom</Badge>
                  )}
                </button>
              )}

              {/* Per-Variant Art Cards */}
              {showPerVariant && (
                <div className="grid grid-cols-1 gap-3">
                  {variantKeys.map(vk => {
                    const vInfo = variants[vk];
                    const stockBadge = getStockBadge(vk);
                    const hasCustomArt = !!variantArt[vk];
                    const artUrl = getArtForVariant(vk);
                    const isUploading = variantUploading[vk];

                    return (
                      <div
                        key={vk}
                        className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${activeVariant === vk
                          ? 'border-purple-500/60 bg-purple-500/10'
                          : 'border-gray-700 bg-gray-800/40 hover:border-gray-600'
                          } ${stockBadge.label === 'Out of Stock' || stockBadge.label === 'Discontinued' ? 'opacity-50' : ''}`}
                      >
                        {/* Variant Art Thumbnail */}
                        <div
                          onClick={() => {
                            if (!isUploading) {
                              const ref = variantFileRefs.current[vk];
                              if (ref) ref.click();
                            }
                          }}
                          className="w-14 h-14 rounded-lg border border-gray-600 overflow-hidden flex-shrink-0 cursor-pointer hover:border-purple-400 transition-all relative"
                        >
                          <input
                            ref={el => variantFileRefs.current[vk] = el}
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => handleVariantUpload(e.target.files[0], vk)}
                          />
                          {isUploading ? (
                            <div className="w-full h-full flex items-center justify-center bg-gray-700">
                              <Loader2 className="w-5 h-5 text-purple-400 animate-spin" />
                            </div>
                          ) : artUrl ? (
                            <img src={artUrl} alt={vk} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-gray-700 text-gray-500">
                              <Upload className="w-4 h-4" />
                            </div>
                          )}
                          {hasCustomArt && (
                            <div className="absolute -top-1 -right-1 w-3 h-3 bg-purple-500 rounded-full border border-gray-800" />
                          )}
                        </div>

                        {/* Variant Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-white text-sm font-medium truncate">
                              {vInfo?.name || vk}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${stockBadge.color}`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${stockBadge.dot}`} />
                              {stockBadge.label}
                            </span>
                            {hasCustomArt ? (
                              <span className="text-xs text-purple-400">Custom art</span>
                            ) : primaryArt ? (
                              <span className="text-xs text-gray-500">Using primary</span>
                            ) : null}
                          </div>
                        </div>

                        {/* Preview this variant */}
                        <button
                          onClick={() => {
                            setActiveVariant(vk);
                          }}
                          className={`text-xs px-3 py-1.5 rounded-lg transition-all ${activeVariant === vk
                            ? 'bg-purple-500 text-white'
                            : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                            }`}
                        >
                          <Eye className="w-3.5 h-3.5" />
                        </button>

                        {/* Remove custom art */}
                        {hasCustomArt && (
                          <button
                            onClick={() => {
                              setVariantArt(prev => {
                                const next = { ...prev };
                                delete next[vk];
                                return next;
                              });
                              toast.success(`Reverted ${vk} to primary art`);
                            }}
                            className="text-gray-500 hover:text-red-400 transition-all"
                            title="Remove custom art"
                          >
                            <XCircle className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Right: Mockup Preview */}
            <div>
              {/* Variant selector tabs (if multiple) */}
              {variantKeys.length > 1 && primaryArt && (
                <div className="flex gap-1 mb-3 overflow-x-auto pb-1">
                  {variantKeys.map(vk => {
                    const stockBadge = getStockBadge(vk);
                    return (
                      <button
                        key={vk}
                        onClick={() => setActiveVariant(vk)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all flex items-center gap-1.5 ${activeVariant === vk
                          ? 'bg-purple-500/30 text-purple-300 border border-purple-500/50'
                          : 'bg-gray-800 text-gray-400 border border-gray-700 hover:border-gray-500'
                          }`}
                      >
                        <span className={`w-1.5 h-1.5 rounded-full ${stockBadge.dot}`} />
                        {vk}
                      </button>
                    );
                  })}
                </div>
              )}

              <MockupPreview
                productType={selectedType}
                artImage={getArtForVariant(activeVariant)}
                mockupData={printfulMockupData}
                mockupLoading={mockupLoading}
              />
            </div>
          </div>
        </div>

        {/* Step 3: Product Details + Pricing */}
        <div className="mb-8">
          <h3 className="text-white font-semibold mb-3 flex items-center">
            <span className="w-7 h-7 bg-purple-600 rounded-full text-white text-sm flex items-center justify-center mr-2">3</span>
            Product Details & Pricing
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className="text-gray-400 text-sm mb-1 block">Product Name *</label>
                <input
                  type="text"
                  value={productName}
                  onChange={(e) => setProductName(e.target.value)}
                  placeholder={catalogProduct ? `e.g. My ${catalogProduct.name}` : 'e.g. Epic Hero Mug'}
                  className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-4 py-3 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                  maxLength={80}
                />
              </div>
              <div>
                <label className="text-gray-400 text-sm mb-1 block">Description</label>
                <textarea
                  value={productDesc}
                  onChange={(e) => setProductDesc(e.target.value)}
                  placeholder="Describe your product..."
                  className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-4 py-3 h-24 resize-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                  maxLength={300}
                />
              </div>
            </div>

            {/* Pricing & Royalty Panel */}
            <div className="bg-gray-800/60 rounded-2xl p-6 border border-gray-700">
              <h4 className="text-white font-semibold mb-4 flex items-center">
                <Calculator className="w-5 h-5 mr-2 text-amber-400" />
                Pricing & Royalty Calculator
              </h4>

              {/* Store Price Slider */}
              <div className="mb-5">
                <label className="text-gray-400 text-sm mb-2 block">Set Store Price: ₹{totalPrice}</label>
                <input
                  type="range"
                  min={basePrice}
                  max={basePrice + 2000}
                  step={10}
                  value={totalPrice}
                  onChange={(e) => setCreatorMarkup(Math.max(0, Number(e.target.value) - basePrice))}
                  className="w-full accent-purple-500"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>₹{basePrice}</span>
                  <span>₹{basePrice + 1000}</span>
                  <span>₹{basePrice + 2000}</span>
                </div>
              </div>

              {/* Cost Breakdown */}
              {costLoading ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="w-5 h-5 animate-spin text-purple-400" />
                  <span className="text-gray-400 text-sm ml-2">Loading costs...</span>
                </div>
              ) : costData && costData.variant_costs?.length > 0 ? (() => {
                // Use the default/median variant for display
                const defaultVariant = costData.variant_costs.find(v => v.size === 'M') || costData.variant_costs[0];
                const prodCost = defaultVariant.production_cost_inr;
                const shippingCost = defaultVariant.shipping_estimate_inr;
                const platformFee = Math.round(totalPrice * (costData.platform_fee_percent / 100));
                const totalCost = prodCost + shippingCost + platformFee;
                const netProfit = Math.max(totalPrice - totalCost, 0);
                const royalty = Math.round(netProfit * (costData.royalty_percent / 100));

                return (
                  <>
                    {/* Line-by-line breakdown */}
                    <div className="space-y-2 mb-4 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-400">Production Cost</span>
                        <span className="text-red-400 font-mono">- ₹{prodCost}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400 flex items-center gap-1">
                          Shipping (est.)
                          <span className="group relative">
                            <Info className="w-3 h-3 text-gray-600 cursor-help" />
                            <span className="hidden group-hover:block absolute bottom-5 left-0 w-48 text-xs bg-gray-900 border border-gray-700 rounded-lg p-2 text-gray-300 z-10">
                              {costData.shipping_note}
                            </span>
                          </span>
                        </span>
                        <span className="text-red-400 font-mono">- ₹{shippingCost}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Platform Fee ({costData.platform_fee_percent}%)</span>
                        <span className="text-red-400 font-mono">- ₹{platformFee}</span>
                      </div>
                      <hr className="border-gray-700" />
                      <div className="flex justify-between">
                        <span className="text-gray-400">Total Cost</span>
                        <span className="text-gray-300 font-mono">₹{totalCost}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-white font-medium">Store Price</span>
                        <span className="text-amber-400 font-mono font-bold">₹{totalPrice}</span>
                      </div>
                      <hr className="border-gray-700" />
                      <div className="flex justify-between">
                        <span className="text-gray-400">Net Profit</span>
                        <span className={`font-mono font-bold ${netProfit > 0 ? 'text-green-400' : 'text-red-400'}`}>
                          ₹{netProfit}
                        </span>
                      </div>
                      <div className="flex justify-between bg-purple-500/10 border border-purple-500/30 rounded-lg px-3 py-2">
                        <span className="text-purple-300 font-medium">Your Royalty ({costData.royalty_percent}%)</span>
                        <span className="text-purple-300 font-mono font-bold text-lg">₹{royalty}</span>
                      </div>
                    </div>

                    {/* Per-variant table (collapsible) */}
                    {costData.variant_costs.length > 1 && (
                      <details className="mt-3">
                        <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-300 transition-colors">
                          View per-variant breakdown ({costData.variant_costs.length} variants)
                        </summary>
                        <div className="mt-2 overflow-x-auto">
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="border-b border-gray-700">
                                <th className="text-left py-1.5 text-gray-500 font-medium">Size</th>
                                <th className="text-right py-1.5 text-gray-500 font-medium">Prod.</th>
                                <th className="text-right py-1.5 text-gray-500 font-medium">Ship.</th>
                                <th className="text-right py-1.5 text-gray-500 font-medium">Fee</th>
                                <th className="text-right py-1.5 text-gray-500 font-medium">Net</th>
                                <th className="text-right py-1.5 text-purple-400 font-medium">Royalty</th>
                              </tr>
                            </thead>
                            <tbody>
                              {costData.variant_costs.map(vc => {
                                const pf = Math.round(totalPrice * (costData.platform_fee_percent / 100));
                                const tc = vc.production_cost_inr + vc.shipping_estimate_inr + pf;
                                const np = Math.max(totalPrice - tc, 0);
                                const ry = Math.round(np * (costData.royalty_percent / 100));
                                return (
                                  <tr key={vc.size} className="border-b border-gray-800">
                                    <td className="py-1.5 text-gray-300">{vc.size}</td>
                                    <td className="py-1.5 text-right text-gray-400 font-mono">₹{vc.production_cost_inr}</td>
                                    <td className="py-1.5 text-right text-gray-400 font-mono">₹{vc.shipping_estimate_inr}</td>
                                    <td className="py-1.5 text-right text-gray-400 font-mono">₹{pf}</td>
                                    <td className={`py-1.5 text-right font-mono ${np > 0 ? 'text-green-400' : 'text-red-400'}`}>₹{np}</td>
                                    <td className="py-1.5 text-right text-purple-300 font-mono font-bold">₹{ry}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </details>
                    )}
                  </>
                );
              })() : (
                /* Fallback if no cost data */
                <div className="space-y-3 mb-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Base Price (fixed)</span>
                    <span className="text-gray-300 font-mono">₹{basePrice}</span>
                  </div>
                  <div className="flex justify-between text-sm items-center">
                    <span className="text-gray-400">Your Markup</span>
                    <span className="text-green-400 font-mono font-bold">+ ₹{creatorMarkup}</span>
                  </div>
                  <hr className="border-gray-700" />
                  <div className="flex justify-between text-lg font-bold">
                    <span className="text-white">Store Price</span>
                    <span className="text-amber-400 font-mono">₹{totalPrice}</span>
                  </div>
                </div>
              )}

              <p className="text-gray-500 text-xs mt-4">
                Royalty = {costData?.royalty_percent || 30}% × Net Profit (after production, shipping & fees).
                Prices update in real-time based on Printful data.
              </p>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <button
          onClick={handleSubmit}
          disabled={submitting || (!primaryArt && Object.keys(variantArt).length === 0) || !productName.trim()}
          className="w-full md:w-auto bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white px-10 py-4 rounded-xl font-bold text-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg shadow-purple-500/20"
        >
          {submitting ? (
            <><Loader2 className="w-5 h-5 animate-spin" /> Submitting...</>
          ) : (
            <><Package className="w-5 h-5" /> Submit for Review</>
          )}
        </button>
      </div>

      {/* My Submissions */}
      <div className="glass-card p-8 rounded-2xl neon-border">
        <h2 className="text-2xl font-bold text-white flex items-center mb-6">
          <Package className="w-6 h-6 mr-3 text-purple-400" />
          My Submissions ({myProducts.length})
        </h2>

        {loadingProducts ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-purple-400 animate-spin" />
          </div>
        ) : myProducts.length === 0 ? (
          <div className="text-center py-12">
            <ShoppingBag className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 text-lg">No products submitted yet</p>
            <p className="text-gray-500 text-sm mt-1">Create your first merch product above!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {myProducts.map((product) => {
              const status = statusConfig[product.status] || statusConfig.pending_review;
              const StatusIcon = status.icon;
              return (
                <div key={product.id} className="bg-gray-800/60 rounded-xl border border-gray-700 overflow-hidden">
                  <div className="aspect-video bg-gray-900/50 overflow-hidden">
                    {product.image_url || product.mockup_url ? (
                      <img
                        src={product.mockup_url || product.image_url}
                        alt={product.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <ImageIcon className="w-12 h-12 text-gray-600" />
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="text-white font-semibold text-sm truncate flex-1">{product.name}</h3>
                      <span className="text-amber-400 font-bold text-sm ml-2">₹{product.price}</span>
                    </div>
                    <p className="text-gray-500 text-xs mb-3 capitalize">{product.category}</p>
                    <div className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border ${status.color}`}>
                      <StatusIcon className="w-3.5 h-3.5" />
                      {status.label}
                    </div>
                    {product.status === 'rejected' && product.rejection_reason && (
                      <p className="text-red-400/70 text-xs mt-2 italic">"{product.rejection_reason}"</p>
                    )}
                    {product.status === 'approved' && (
                      <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                        <span>Sales: {product.sales_count || 0}</span>
                        <span>Earned: ₹{product.creator_earnings || 0}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};


const DashboardSimple = () => {
  const { user, token, API } = useContext(AuthContext);
  const [stats, setStats] = useState({
    comics_count: 0,
    total_likes: 0,
    total_comments: 0
  });
  const [myComics, setMyComics] = useState([]);
  const [myProjects, setMyProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('comics');
  const [merchEnabled, setMerchEnabled] = useState(false);

  useEffect(() => {
    fetchDashboardData();
    // Check if merch is enabled
    axios.get(`${BACKEND_URL}/api/settings/features`).then(res => {
      setMerchEnabled(res.data.merch_enabled || false);
    }).catch(() => { });
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      // Fetch dashboard stats
      const statsResponse = await axios.get(`${API}/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(statsResponse.data);

      // Fetch user's comics (streamlined)
      const comicsResponse = await axios.get(`${API}/streamlined-comics?author_id=${user.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyComics(comicsResponse.data);

      // Fetch user's saved projects
      try {
        const projectsResponse = await axios.get(`${API}/projects/my`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setMyProjects(projectsResponse.data.projects || []);
      } catch (e) {
        console.log('Projects fetch error:', e);
      }

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteComic = async (comicId) => {
    if (!window.confirm('Are you sure you want to delete this comic?')) {
      return;
    }

    try {
      await axios.delete(`${API}/streamlined-comics/${comicId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Remove comic from state
      setMyComics(prev => prev.filter(comic => comic.id !== comicId));

      // Update stats
      setStats(prev => ({
        ...prev,
        comics_count: prev.comics_count - 1
      }));

      toast.success('Comic deleted successfully');
    } catch (error) {
      console.error('Error deleting comic:', error);
      toast.error('Failed to delete comic');
    }
  };

  const handleDeleteProject = async (projectId) => {
    if (!window.confirm('Are you sure you want to delete this artwork?')) {
      return;
    }

    try {
      await axios.delete(`${API}/projects/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setMyProjects(prev => prev.filter(project => project.id !== projectId));
      toast.success('Artwork deleted successfully');
    } catch (error) {
      console.error('Error deleting project:', error);
      toast.error('Failed to delete artwork');
    }
  };

  const handleToggleVisibility = async (projectId, currentStatus) => {
    const nextStatus = !currentStatus;
    try {
      await axios.patch(
        `${API}/projects/${projectId}/visibility`,
        { is_public: nextStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMyProjects(prev =>
        prev.map(p => (p.id === projectId ? { ...p, is_public: nextStatus } : p))
      );
      toast.success(
        nextStatus
          ? 'Artwork is now Public & visible on the Landing Page!'
          : 'Artwork set to Private'
      );
    } catch (error) {
      console.error('Error toggling artwork visibility:', error);
      toast.error('Failed to update artwork visibility');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen relative overflow-hidden hero-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-16 h-16 text-purple-400 animate-spin mx-auto mb-4" />
          <p className="text-gray-400 text-lg">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative overflow-hidden hero-background">
      <div className="max-w-7xl mx-auto px-4 py-24 relative z-10">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-12 animate-fadeIn">
          <div>
            <h1 className="text-5xl font-bold text-white mb-3">Creator Dashboard</h1>
            <p className="text-gray-400 text-lg">Welcome back, <span className="text-purple-400 font-semibold">{user?.display_name || user?.username}</span>! Manage your comics and track your progress.</p>
          </div>
          <div className="mt-6 md:mt-0">
            <Link to="/create-comic" className="btn-primary h-12 px-8 inline-flex items-center justify-center rounded-lg">
              <Plus className="w-5 h-5 mr-2" />
              Create New Comic
            </Link>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
          <div className="glass-card p-6 rounded-2xl neon-border animate-fadeIn hover:scale-105 transition-transform duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm font-medium mb-2">Comics Published</p>
                <p className="text-4xl font-bold text-white">{stats.comics_count}</p>
              </div>
              <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-purple-800 rounded-2xl flex items-center justify-center shadow-lg shadow-purple-500/50">
                <BookOpen className="w-8 h-8 text-white" />
              </div>
            </div>
          </div>

          <div className="glass-card p-6 rounded-2xl neon-border animate-fadeIn hover:scale-105 transition-transform duration-300" style={{ animationDelay: '0.1s' }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm font-medium mb-2">Saved Artworks</p>
                <p className="text-4xl font-bold text-white">{myProjects.length}</p>
              </div>
              <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-yellow-500 rounded-2xl flex items-center justify-center shadow-lg shadow-orange-500/50">
                <PenTool className="w-8 h-8 text-white" />
              </div>
            </div>
          </div>

          <div className="glass-card p-6 rounded-2xl neon-border animate-fadeIn hover:scale-105 transition-transform duration-300" style={{ animationDelay: '0.2s' }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm font-medium mb-2">Total Likes</p>
                <p className="text-4xl font-bold text-white">{stats.total_likes}</p>
              </div>
              <div className="w-16 h-16 bg-gradient-to-br from-pink-600 to-red-600 rounded-2xl flex items-center justify-center shadow-lg shadow-pink-500/50">
                <Heart className="w-8 h-8 text-white" />
              </div>
            </div>
          </div>

          <div className="glass-card p-6 rounded-2xl neon-border animate-fadeIn hover:scale-105 transition-transform duration-300" style={{ animationDelay: '0.3s' }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm font-medium mb-2">Total Comments</p>
                <p className="text-4xl font-bold text-white">{stats.total_comments}</p>
              </div>
              <div className="w-16 h-16 bg-gradient-to-br from-cyan-600 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg shadow-cyan-500/50">
                <MessageCircle className="w-8 h-8 text-white" />
              </div>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex space-x-4 mb-8 flex-wrap gap-y-2">
          <button
            onClick={() => setActiveTab('comics')}
            className={`px-6 py-3 rounded-xl font-medium transition-all ${activeTab === 'comics'
              ? 'bg-purple-600 text-white shadow-lg shadow-purple-500/30'
              : 'bg-gray-800/50 text-gray-400 hover:text-white hover:bg-gray-700/50'
              }`}
            data-testid="tab-comics"
          >
            <BookOpen className="w-5 h-5 inline-block mr-2" />
            Published Comics ({myComics.length})
          </button>
          <button
            onClick={() => setActiveTab('artworks')}
            className={`px-6 py-3 rounded-xl font-medium transition-all ${activeTab === 'artworks'
              ? 'bg-orange-500 text-white shadow-lg shadow-orange-500/30'
              : 'bg-gray-800/50 text-gray-400 hover:text-white hover:bg-gray-700/50'
              }`}
            data-testid="tab-artworks"
          >
            <PenTool className="w-5 h-5 inline-block mr-2" />
            My Artworks ({myProjects.length})
          </button>
          {merchEnabled && (
            <button
              onClick={() => setActiveTab('merch')}
              className={`px-6 py-3 rounded-xl font-medium transition-all ${activeTab === 'merch'
                ? 'bg-amber-500 text-white shadow-lg shadow-amber-500/30'
                : 'bg-gray-800/50 text-gray-400 hover:text-white hover:bg-gray-700/50'
                }`}
              data-testid="tab-merch"
            >
              <ShoppingBag className="w-5 h-5 inline-block mr-2" />
              Merch
            </button>
          )}
        </div>

        {/* Content Section - Comics */}
        {activeTab === 'comics' && (
          <div className="glass-card p-8 rounded-2xl neon-border animate-fadeIn" style={{ animationDelay: '0.3s' }}>
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-3xl font-bold text-white flex items-center">
                <TrendingUp className="w-8 h-8 mr-3 text-purple-400" />
                Your Comics ({myComics.length})
              </h2>
            </div>

            {myComics.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-purple-600/20 to-cyan-600/20 rounded-3xl flex items-center justify-center">
                  <BookOpen className="w-12 h-12 text-purple-400" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">No comics yet</h3>
                <p className="text-gray-400 mb-8 text-lg">Start sharing your comic stories with the world!</p>
                <Link to="/create-comic" className="btn-primary h-12 px-8 inline-flex items-center justify-center rounded-lg">
                  <Sparkles className="w-5 h-5 mr-2" />
                  Create Your First Comic
                </Link>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {myComics.map((comic, index) => (
                  <div key={comic.id} className="comic-card animate-fadeIn relative" style={{ animationDelay: `${index * 0.1}s` }}>
                    {/* Comic Image */}
                    <div className="relative overflow-hidden rounded-t-2xl aspect-[3/4]">
                      {comic.episodes?.[0]?.slides?.[0] ? (
                        <img
                          src={comic.episodes[0].slides[0]}
                          alt={comic.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-purple-900/50 to-blue-900/50 flex items-center justify-center">
                          <Sparkles className="w-16 h-16 text-purple-400" />
                        </div>
                      )}
                    </div>

                    {/* Comic Info */}
                    <div className="p-5">
                      <h3 className="text-xl font-bold text-white mb-2 line-clamp-1">{comic.title}</h3>
                      <p className="text-gray-400 text-sm mb-4 line-clamp-2">{comic.description}</p>

                      {/* Stats */}
                      <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                        <div className="flex items-center space-x-4">
                          <span className="flex items-center space-x-1 text-gray-400">
                            <Heart className="w-4 h-4" />
                            <span>{comic.likes_count || 0}</span>
                          </span>
                          <span className="flex items-center space-x-1 text-gray-400">
                            <MessageCircle className="w-4 h-4" />
                            <span>{comic.comments_count || 0}</span>
                          </span>
                        </div>
                        <span className="flex items-center space-x-1 text-gray-500">
                          <Calendar className="w-4 h-4" />
                          <span className="text-xs">{formatDate(comic.created_at)}</span>
                        </span>
                      </div>

                      {/* Categories */}
                      {comic.categories && comic.categories.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-4">
                          {comic.categories.slice(0, 2).map((category, idx) => (
                            <Badge key={idx} className="bg-purple-600/30 text-purple-300 border-purple-500/50 text-xs">
                              {category}
                            </Badge>
                          ))}
                          {comic.categories.length > 2 && (
                            <Badge className="bg-gray-700/50 text-gray-400 text-xs">
                              +{comic.categories.length - 2}
                            </Badge>
                          )}
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="flex space-x-2 relative z-10">
                        <Link
                          to={`/streamlined-comic/${comic.id}`}
                          className="flex-1 bg-purple-600/40 hover:bg-purple-600/60 text-white text-sm h-10 inline-flex items-center justify-center rounded-lg px-4 transition-all border-2 border-purple-500/50 hover:border-purple-400"
                          data-testid={`view-comic-${comic.id}`}
                        >
                          <Eye className="w-4 h-4 mr-2" />
                          <span>View</span>
                        </Link>
                        <Link
                          to={`/edit-comic/${comic.id}`}
                          className="bg-gray-700/60 hover:bg-gray-600/70 text-white h-10 inline-flex items-center justify-center rounded-lg px-4 transition-all border-2 border-gray-600 hover:border-gray-500"
                          data-testid={`edit-comic-${comic.id}`}
                        >
                          <Edit className="w-4 h-4 mr-2" />
                          <span>Edit</span>
                        </Link>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            handleDeleteComic(comic.id);
                          }}
                          className="bg-red-600/30 hover:bg-red-600/50 text-red-300 hover:text-red-200 border-2 border-red-500/50 hover:border-red-400 h-10 inline-flex items-center justify-center rounded-lg px-4 transition-all"
                          data-testid={`delete-comic-${comic.id}`}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          <span>Delete</span>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Content Section - Artworks */}
        {activeTab === 'artworks' && (
          <div className="glass-card p-8 rounded-2xl neon-border animate-fadeIn" style={{ animationDelay: '0.3s' }}>
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-3xl font-bold text-white flex items-center">
                <PenTool className="w-8 h-8 mr-3 text-orange-400" />
                My Artworks ({myProjects.length})
              </h2>
              <Link
                to="/comica"
                state={{ newProject: true }}
                className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-2 rounded-lg font-medium transition-all inline-flex items-center"
                data-testid="new-artwork-btn"
              >
                <Plus className="w-5 h-5 mr-2" />
                New Artwork
              </Link>
            </div>

            {myProjects.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-orange-500/20 to-yellow-500/20 rounded-3xl flex items-center justify-center">
                  <PenTool className="w-12 h-12 text-orange-400" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">No saved artworks</h3>
                <p className="text-gray-400 mb-8 text-lg">Start creating and save your work in the ComicaPro editor!</p>
                <Link to="/comica" state={{ newProject: true }} className="bg-gradient-to-r from-orange-500 to-yellow-500 text-white h-12 px-8 inline-flex items-center justify-center rounded-lg font-medium">
                  <Sparkles className="w-5 h-5 mr-2" />
                  Create New Comic Blueprint
                </Link>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {myProjects.map((project, index) => (
                  <div key={project.id} className="group bg-gray-800/60 rounded-2xl overflow-hidden border border-gray-700/50 hover:border-orange-500/50 transition-all hover:shadow-lg hover:shadow-orange-500/10" style={{ animationDelay: `${index * 0.1}s` }}>
                    {/* Artwork Thumbnail */}
                    <div className="relative aspect-video bg-gray-900/50 overflow-hidden">
                      {/* Visibility Badge Toggle */}
                      <div className="absolute top-2 right-2 z-10">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            handleToggleVisibility(project.id, project.is_public);
                          }}
                          className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold backdrop-blur-md border shadow-lg transition-all cursor-pointer ${
                            project.is_public
                              ? 'bg-emerald-600/90 hover:bg-emerald-500 text-white border-emerald-400/50 shadow-emerald-500/30'
                              : 'bg-gray-900/90 hover:bg-gray-800 text-gray-300 border-gray-600/60'
                          }`}
                          title={project.is_public ? "Public on Landing Page. Click to set Private" : "Private. Click to show on Landing Page"}
                          data-testid={`toggle-visibility-${project.id}`}
                        >
                          {project.is_public ? (
                            <>
                              <Globe className="w-3 h-3 text-emerald-300 animate-pulse" />
                              <span>Public</span>
                            </>
                          ) : (
                            <>
                              <Lock className="w-3 h-3 text-gray-400" />
                              <span>Private</span>
                            </>
                          )}
                        </button>
                      </div>

                      {project.thumbnail ? (
                        <img
                          src={project.thumbnail}
                          alt={project.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <ImageIcon className="w-16 h-16 text-gray-600" />
                        </div>
                      )}
                      {/* Hover overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-center pb-4">
                        <Link
                          to="/comica"
                          state={{ project }}
                          className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-2 rounded-lg font-medium transition-all inline-flex items-center transform translate-y-4 group-hover:translate-y-0"
                          data-testid={`load-project-${project.id}`}
                        >
                          <Edit className="w-4 h-4 mr-2" />
                          Continue Editing
                        </Link>
                      </div>
                    </div>

                    {/* Artwork Info */}
                    <div className="p-4">
                      <h3 className="text-lg font-semibold text-white mb-1 line-clamp-1">{project.title}</h3>
                      <p className="text-gray-400 text-sm mb-3 line-clamp-2">{project.description || 'No description'}</p>

                      <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
                        <span className="flex items-center">
                          <Calendar className="w-3 h-3 mr-1" />
                          {formatDate(project.created_at)}
                        </span>
                        {project.views_count > 0 && (
                          <span className="flex items-center">
                            <Eye className="w-3 h-3 mr-1" />
                            {project.views_count} views
                          </span>
                        )}
                      </div>

                      {/* Action Buttons */}
                      <div className="flex space-x-2">
                        <Link
                          to="/comica"
                          state={{ project }}
                          className="flex-1 bg-orange-500/30 hover:bg-orange-500/50 text-orange-300 hover:text-orange-200 text-sm h-9 inline-flex items-center justify-center rounded-lg transition-all border border-orange-500/50 hover:border-orange-400"
                          data-testid={`edit-artwork-${project.id}`}
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          Edit
                        </Link>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleVisibility(project.id, project.is_public);
                          }}
                          className={`text-sm h-9 px-3 rounded-lg transition-all border flex items-center justify-center gap-1.5 ${
                            project.is_public
                              ? 'bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 border-emerald-500/40'
                              : 'bg-gray-700/40 hover:bg-gray-700/60 text-gray-300 border-gray-600/50'
                          }`}
                          title={project.is_public ? "Public on Landing Page. Click to set Private" : "Private. Click to show on Landing Page"}
                        >
                          {project.is_public ? <Globe className="w-4 h-4 text-emerald-400" /> : <Lock className="w-4 h-4 text-gray-400" />}
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteProject(project.id);
                          }}
                          className="bg-red-600/20 hover:bg-red-600/40 text-red-400 hover:text-red-300 text-sm h-9 px-3 rounded-lg transition-all border border-red-500/30 hover:border-red-400/50"
                          data-testid={`delete-artwork-${project.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Content Section - Merch */}
        {activeTab === 'merch' && merchEnabled && (
          <MerchTab token={token} API={API} user={user} />
        )}
      </div>
    </div>
  );
};

export default DashboardSimple;
